import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {colors} from '../../../theme/colors';

interface EmailReply {
  messageId: string;
  from: string;
  subject: string;
  snippet: string;
  receivedAt: string;
}

interface EmailEvent {
  eventType: string;
  messageId: string;
  email: string;
  timestamp: string;
}

interface Props {
  emailThreadId?: string;
  emailReplies?: EmailReply[];
  emailEvents?: EmailEvent[];
  recipientEmail?: string;
}

const EVENT_LABELS: Record<string, {label: string; color: 'success' | 'info' | 'warning' | 'error'}> = {
  delivered: {label: 'Delivered', color: 'success'},
  opened: {label: 'Opened', color: 'info'},
  clicked: {label: 'Clicked', color: 'info'},
  bounced: {label: 'Bounced', color: 'error'},
  dropped: {label: 'Dropped', color: 'error'},
  deferred: {label: 'Deferred', color: 'warning'},
};

export const EmailThreadPanel: React.FC<Props> = ({
  emailThreadId,
  emailReplies = [],
  emailEvents = [],
  recipientEmail,
}) => {
  if (!emailThreadId) return null;

  const lastEvent = emailEvents.length > 0 ? emailEvents[emailEvents.length - 1] : null;
  const lastReply = emailReplies.length > 0 ? emailReplies[emailReplies.length - 1] : null;
  const hasReply = emailReplies.length > 0;

  return (
    <Paper variant="outlined" sx={{mx: 2, mb: 2, overflow: 'hidden'}}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: hasReply ? colors.green[50] : colors.blue[50],
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {hasReply ? (
          <MarkEmailReadIcon sx={{color: colors.green[700], fontSize: 20}} />
        ) : (
          <EmailIcon sx={{color: colors.blue[700], fontSize: 20}} />
        )}
        <Typography variant="subtitle2" sx={{fontWeight: 600, flex: 1}}>
          {hasReply ? 'Reply received' : 'Waiting for email reply'}
        </Typography>
        {recipientEmail && (
          <Typography variant="caption" color="text.secondary">
            {recipientEmail}
          </Typography>
        )}
      </Box>

      <Box sx={{p: 2}}>
        {/* Latest event chips */}
        {emailEvents.length > 0 && (
          <Stack direction="row" spacing={0.5} sx={{mb: 1.5, flexWrap: 'wrap'}}>
            {emailEvents.slice(-5).map((evt, i) => {
              const cfg = EVENT_LABELS[evt.eventType] ?? {label: evt.eventType, color: 'info' as const};
              return (
                <Chip
                  key={i}
                  size="small"
                  label={cfg.label}
                  color={cfg.color}
                  variant="outlined"
                />
              );
            })}
          </Stack>
        )}

        {/* Replies */}
        {emailReplies.map((reply, i) => (
          <Box key={reply.messageId}>
            {i > 0 && <Divider sx={{my: 1}} />}
            <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1}}>
              <MailOutlineIcon sx={{fontSize: 18, color: 'text.secondary', mt: 0.3}} />
              <Box sx={{flex: 1, minWidth: 0}}>
                <Typography variant="caption" color="text.secondary">
                  {reply.from} &middot;{' '}
                  {new Date(reply.receivedAt).toLocaleString()}
                </Typography>
                <Typography variant="body2" sx={{mt: 0.25}}>
                  {reply.snippet}
                </Typography>
              </Box>
            </Box>
          </Box>
        ))}

        {/* No replies yet */}
        {emailReplies.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No replies yet.
            {lastEvent && ` Last event: ${EVENT_LABELS[lastEvent.eventType]?.label ?? lastEvent.eventType}`}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};
